import {
  CUSTOMER_EVIDENCE_FIELDS,
  personaEvidenceSummary,
  type CustomerEvidenceAggregate,
  type CustomerEvidenceImport,
  type EvidenceField,
} from "@shared/customer-evidence";

export type EvidenceTableRow = {
  label: string;
  count?: number;
  pct?: number;
  denominator?: number;
  note?: string;
};

export type EvidenceTableGroup = {
  key: string;
  title: string;
  basis: string;
  rows: EvidenceTableRow[];
};

export const EVIDENCE_FIELD_LABELS: Record<EvidenceField, string> = {
  jobTitle: "Job title",
  seniority: "Seniority",
  jobFunction: "Job function",
  department: "Department",
  industry: "Industry",
  subIndustry: "Sub-industry",
  employeeRange: "Employee range",
  revenueRange: "Revenue range",
  companyName: "Company name",
  companyState: "Company state",
  companyCountry: "Company country",
  businessModel: "Business model",
  matchStatus: "Match status",
  personMoved: "Person moved",
};

const formatNumber = (value: number) => value.toLocaleString();

function numericValue(value: unknown): number | undefined {
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (typeof value === "string" && value.trim() !== "" && Number.isFinite(Number(value))) return Number(value);
  return undefined;
}

function rowsFromRecord(record: Record<string, unknown>, denominator?: number): EvidenceTableRow[] {
  return Object.entries(record).map(([label, value]) => {
    const count = numericValue(value);
    return count === undefined
      ? { label, note: typeof value === "string" ? value : String(JSON.stringify(value)) }
      : { label, count, ...(denominator === undefined ? {} : {
        denominator,
        ...(denominator > 0 ? { pct: Math.round(count / denominator * 10_000) / 100 } : {}),
      }) };
  });
}

function rowsFromList(values: unknown[]): EvidenceTableRow[] {
  return values.map((value, index) => {
    if (value && typeof value === "object" && !Array.isArray(value)) {
      const entry = value as Record<string, unknown>;
      const label = String(entry.label ?? entry.name ?? entry.category ?? entry.title ?? `Group ${index + 1}`);
      const count = numericValue(entry.count ?? entry.rows ?? entry.companies);
      const pct = numericValue(entry.pct);
      const denominator = numericValue(entry.denominator);
      const excluded = new Set(["label", "name", "category", "title", "count", "rows", "companies", "pct", "denominator"]);
      const extra = Object.entries(entry)
        .filter(([key]) => !excluded.has(key))
        .map(([key, extraValue]) => `${key.replace(/[_-]/g, " ")}: ${String(extraValue)}`)
        .join("; ");
      return {
        label,
        ...(count === undefined ? {} : { count }),
        ...(pct === undefined ? {} : { pct }),
        ...(denominator === undefined ? {} : { denominator }),
        ...(extra ? { note: extra } : {}),
      };
    }
    const count = numericValue(value);
    return count === undefined
      ? { label: `Group ${index + 1}`, note: String(value) }
      : { label: `Group ${index + 1}`, count };
  });
}

function optionalRows(value: unknown): EvidenceTableRow[] {
  if (Array.isArray(value)) return rowsFromList(value);
  if (value && typeof value === "object") return rowsFromRecord(value as Record<string, unknown>);
  const count = numericValue(value);
  return count === undefined ? [{ label: "Value", note: String(value) }] : [{ label: "Records", count }];
}

// Historical department sections can be count records rather than distributions.
// Retain list percentages/bases; only derive them when the saved shape omits them.
function highlightRows(value: unknown, denominator: number): (EvidenceTableRow & { count: number; pct: number; denominator: number })[] {
  const rows = Array.isArray(value)
    ? rowsFromList(value)
    : value && typeof value === "object" ? rowsFromRecord(value as Record<string, unknown>, denominator) : [];
  return rows.flatMap((row) => {
    const base = row.denominator ?? denominator;
    if (row.count === undefined || row.count <= 0 || base <= 0) return [];
    const pct = row.pct ?? Math.round(row.count / base * 10_000) / 100;
    return [{ ...row, count: row.count, pct, denominator: base }];
  });
}

export function companyDenominatorBasis(aggregate: CustomerEvidenceAggregate): string {
  const summary = personaEvidenceSummary(aggregate);
  if (summary.company_fields_basis === "eligible_rows") {
    return `Eligible rows: ${formatNumber(summary.denominators.company_fields ?? 0)}. A unique-company denominator is unavailable, so company-field counts use eligible rows instead.`;
  }
  return `Companies: ${formatNumber(summary.denominators.company)}.`;
}

export function personDenominatorBasis(aggregate: CustomerEvidenceAggregate): string {
  return `People: ${formatNumber(personaEvidenceSummary(aggregate).denominators.person)}.`;
}

export function titleDenominatorBasis(aggregate: CustomerEvidenceAggregate): string {
  return `Title-eligible people: ${formatNumber(personaEvidenceSummary(aggregate).denominators.title)}.`;
}

export function getEvidenceDetailGroups(item: CustomerEvidenceImport): EvidenceTableGroup[] {
  const aggregate = personaEvidenceSummary(item.aggregate);
  const companyBasis = companyDenominatorBasis(item.aggregate);
  const titleBasis = titleDenominatorBasis(item.aggregate);
  const companyDenominator = aggregate.company_fields_basis === "eligible_rows"
    ? aggregate.denominators.company_fields
    : aggregate.denominators.company;
  const groups: EvidenceTableGroup[] = [
    {
      key: "missing",
      title: "Missing values",
      basis: `Title and seniority missing counts use title-eligible people. Other missing counts use eligible people, before company deduplication. ${personDenominatorBasis(item.aggregate)} ${titleBasis}`,
      rows: Object.entries(aggregate.missing).map(([field, count]) => {
        const denominator = ["jobTitle", "seniority"].includes(field) ? aggregate.denominators.title : aggregate.denominators.person;
        return { label: EVIDENCE_FIELD_LABELS[field as EvidenceField] ?? field, count, denominator,
          ...(denominator > 0 ? { pct: Math.round(count / denominator * 10_000) / 100 } : {}) };
      }),
    },
    {
      key: "business-model",
      title: "Business model",
      basis: companyBasis,
      rows: rowsFromRecord(aggregate.business_model, companyDenominator),
    },
    {
      key: "seniority",
      title: "Seniority",
      basis: titleBasis,
      rows: rowsFromRecord(aggregate.seniority ?? {}, aggregate.denominators.title),
    },
    {
      key: "industries",
      title: "Industry",
      basis: companyBasis,
      rows: rowsFromList(aggregate.industries),
    },
    {
      key: "title-buckets",
      title: "Title groups and safe examples",
      basis: titleBasis,
      rows: aggregate.title_buckets.map((bucket) => ({
        label: bucket.label,
        count: bucket.count,
        pct: bucket.pct,
        denominator: bucket.denominator,
        note: bucket.examples.length ? `Examples: ${bucket.examples.join(", ")}` : undefined,
      })),
    },
    {
      key: "top-titles",
      title: "Most common titles",
      basis: titleBasis,
      rows: aggregate.top_titles.map(({ title, count }) => ({ label: title, count, denominator: aggregate.denominators.title,
        ...(aggregate.denominators.title > 0 ? { pct: Math.round(count / aggregate.denominators.title * 10_000) / 100 } : {}) })),
    },
    {
      key: "company-size",
      title: "Company size",
      basis: companyBasis,
      rows: aggregate.company_size.map(({ label, count, pct, denominator }) => ({
        label,
        count,
        pct,
        denominator,
      })),
    },
    {
      key: "countries",
      title: "Countries",
      basis: companyBasis,
      rows: rowsFromRecord(aggregate.geography?.country ?? {}, companyDenominator),
    },
    {
      key: "states",
      title: "States and regions",
      basis: companyBasis,
      rows: rowsFromList(aggregate.geography?.top_states ?? []),
    },
    ...Object.entries(aggregate.optional ?? {}).map(([key, value]) => ({
      key: `optional-${key}`,
      title: key.replace(/[_-]/g, " ").replace(/\b\w/g, (letter) => letter.toUpperCase()),
      basis: optionalBasis(key, value, companyBasis, personDenominatorBasis(item.aggregate)),
      rows: optionalRows(value),
    })),
  ];
  return groups.filter((group) => group.rows.length > 0);
}

function optionalBasis(key: string, value: unknown, companyBasis: string, peopleBasis: string): string {
  if (Array.isArray(value)) {
    const denominator = value.find((entry) => entry && typeof entry === "object" && typeof (entry as Record<string, unknown>).denominator === "number");
    if (denominator) {
      return `Section denominator: ${formatNumber((denominator as Record<string, number>).denominator)}.`;
    }
  }
  if (["function", "department"].includes(key)) return peopleBasis;
  return companyBasis;
}

export function getMappedFieldLabels(item: CustomerEvidenceImport): string[] {
  return CUSTOMER_EVIDENCE_FIELDS
    .filter((field) => item.columnMapping?.[field] !== undefined)
    .map((field) => `${EVIDENCE_FIELD_LABELS[field]} (column ${(item.columnMapping[field] ?? 0) + 1})`);
}

export function getUnmappedFieldLabels(item: CustomerEvidenceImport): string[] {
  return CUSTOMER_EVIDENCE_FIELDS
    .filter((field) => item.columnMapping?.[field] === undefined)
    .map((field) => EVIDENCE_FIELD_LABELS[field]);
}

export function getEvidenceSummary(item: CustomerEvidenceImport) {
  const aggregate = personaEvidenceSummary(item.aggregate);
  const leading = <T extends { label: string; count: number }>(rows: T[], excludeOther = false) =>
    rows.filter((row) => row.label.trim() !== "" && !["missing", "no title", ...(excludeOther ? ["other"] : [])].includes(row.label.trim().toLowerCase()))
      .slice().sort((left, right) => right.count - left.count || left.label.localeCompare(right.label)).slice(0, 3);
  const titleLeaders = leading(aggregate.title_buckets, true);
  const industryLeaders = leading(aggregate.industries);
  const sizeLeaders = leading(aggregate.company_size);
  const managementLeaders = item.columnMapping?.seniority === undefined ? []
    : leading(highlightRows(aggregate.seniority, aggregate.denominators.title));
  const departmentLeaders = item.columnMapping?.department === undefined ? []
    : leading(highlightRows(aggregate.optional?.department, aggregate.denominators.person));
  const companyFieldDenominator = aggregate.company_fields_basis === "eligible_rows"
    ? aggregate.denominators.company_fields ?? 0 : aggregate.denominators.company;
  const stateLeaders = item.columnMapping?.companyState === undefined ? []
    : leading(highlightRows(aggregate.geography?.top_states, companyFieldDenominator));
  const topTitle = aggregate.top_titles.slice().sort((left, right) => right.count - left.count)[0];
  const missingTitles = aggregate.title_buckets.find((row) => row.label.toLowerCase() === "no title");
  const observations = [
    titleLeaders.length
      ? `Leading named role groups: ${titleLeaders.slice(0, 2).map((row) => `${row.label} (${row.pct}%)`).join(" and ")} of ${aggregate.denominators.title.toLocaleString()} title-eligible people.`
      : "No named role groups are available in this import.",
    ...(topTitle ? [`Most common retained title: ${topTitle.title}, with ${topTitle.count.toLocaleString()} occurrences.`] : []),
    ...(industryLeaders.length ? [`Leading industries: ${industryLeaders.slice(0, 2).map((row) => `${row.label} (${row.pct}%)`).join(" and ")}. ${companyDenominatorBasis(item.aggregate)}`] : []),
    ...(missingTitles ? [`${missingTitles.count.toLocaleString()} title-eligible people have no title (${missingTitles.pct}%). Missing titles are not a buyer group.`] : []),
  ];
  return {
    aggregate,
    companyBasis: companyDenominatorBasis(item.aggregate),
    peopleBasis: personDenominatorBasis(item.aggregate),
    titleBasis: titleDenominatorBasis(item.aggregate),
    departmentBasis: departmentLeaders.length
      ? `People: ${formatNumber(departmentLeaders[0].denominator)}.`
      : personDenominatorBasis(item.aggregate),
    titleLeaders,
    industryLeaders,
    sizeLeaders,
    managementLeaders,
    departmentLeaders,
    stateLeaders,
    observations,
    details: getEvidenceDetailGroups(item),
  };
}
